//@flow
import React, {useCallback, useEffect, useRef} from 'react';
import {useParams, useHistory, useLocation} from 'react-router-dom';
import {colors} from '../../../../assets/styles/colors';
import {
  makeStyles,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Divider,
} from '@material-ui/core';

import type {TextState, TextAreaTextApi} from '@uiw/react-md-editor';
import {ArrowBack} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {useCrmSegments} from '../../../crm-segment/utils';
import {Autocomplete} from '@material-ui/lab';
import CustomTextEditor from '../../../../elements/CustomTextEditor/CustomTextEditor';
import TiendadaCalendar from '../../../../elements/tiendada-calendar';
import {getStringDate, convertToLocalTime} from '../../../../utils/date';
import {STANDARD_HOUR_OPTIONS} from '../../../multichat/constants/executionTime';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import TiendadaRadioButton from '../../../../elements/tiendada-radio-button';
import {useForm} from 'react-hook-form';
import {useCrmCampaignsActions, useCrmCampaign} from '../../utils';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import CustomChip from '../../../../elements/CustomChip';
import FormField from '../../../blog/components/BlogForm/FormField';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';

import type {
  CreateCampaignWebRequest,
  EditCampaignWebRequest,
} from '../../promises';
import {useWhatsappApiTemplates} from '../../../multichat/chat/utils';
import {useState} from 'react';
import {useMemo} from 'react';
import WhatsAppTemplateRenderer from '../../../../elements/whatsapp-template-renderer';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    padding: '.75rem',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
  },
  header: {
    maxWidth: 1200,
    background: '#f8f9fb',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  root: {
    width: '100%',
    padding: '1rem',
    background: 'white',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
    minHeight: '500px',
    maxWidth: 760,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },

    '&[data-disabled="true"]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: '.5rem',
    position: 'relative',
  },
  sectionInformation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  description: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
  divider: {
    // margin: '.rem 0',
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    aspectRatio: '3/2',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    maxHeight: '200px',
    gap: '.5rem',
    flexDirection: 'column',
    color: colors.primary,
    cursor: 'pointer',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.75rem',
    color: colors.grey700,
    textAlign: 'center',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: colors.error,
    fontSize: '.875rem',
    marginBlock: '1rem',
  },
  actionButton: {
    padding: '5px 15px',
    fontSize: '12px',
    color: '#7B39FF',
    border: `1px solid ${colors.purple3}`,
    fontFamily: 'Poppins',
    fontWeight: 600,
    borderRadius: '20px',
  },
  dateSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100%',

    '&[data-disabled="true"]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  scheduleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flexGrow: 1,
  },
  footer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    alignItems: 'center',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  error: {
    color: colors.error,
    fontSize: '.875rem',
    marginBlock: '.5rem',
  },
}));

export const ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM =
  'enable_whatsapp_web_provider';

const CAMPAIGNS_FORM_FIELDS = {
  NAME: 'name',
  SEGMENT: 'segment',
  CHANNEL: 'channel',
  // EXCLUDE_OPT_OUT: 'exclude_opt_out',
  MESSAGE: 'message',
  SEND_DATE: 'date',
  SEND_TIME: 'send_time',
  INCLUDE_CONTACTS: 'include_contacts',
  PUBLISH_CAMPAIGN: 'publish_campaign',
};

const defaultMessage =
  ' Hola.\nqueremos informarte sobre nuestras últimas novedades.\nContáctanos para más detalles.';

const getDefaultSendTime = (sendAt: string = '2025-01-31T17:00:00Z') => {
  const dateString = new Date(sendAt);
  const hour = dateString
    .getHours()
    .toString()
    .padStart(2, '0');
  const minute = dateString
    .getMinutes()
    .toString()
    .padStart(2, '0');

  return `${hour}:${minute}-05:00`;
};

const isToday = date => {
  if (!date) return false;
  const today = new Date();
  return (
    today.getFullYear() === date.getFullYear() &&
    today.getMonth() === date.getMonth() &&
    today.getDate() === date.getDate()
  );
};

const isDisabled = (optionUtcValue, watchDate) => {
  const currentTime = new Date();
  const optionTime = new Date(
    `${currentTime.toISOString().split('T')[0]}T${optionUtcValue.split('-')[0]}`
  );

  if (isToday(watchDate)) {
    return optionTime <= currentTime;
  }
  return false;
};

export default function CrmCampaignForm() {
  const classes = useStyles();
  const {id} = useParams();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location?.search);

  const enableWhatsappWebProvider = searchParams.has(
    ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const templateComponentsRef = useRef([]);

  const {segments} = useCrmSegments({
    page: 0,
    size: 30,
  });

  const {
    createCrmCampaign,
    loading,
    editCrmCampaign,
  } = useCrmCampaignsActions();

  const {templates} = useWhatsappApiTemplates();

  const isNew = id === 'new';
  const {campaign} = useCrmCampaign(isNew ? undefined : id);

  const {
    register,
    setValue,
    errors,
    clearErrors,
    watch,
    handleSubmit,
  } = useForm({
    defaultValues: {
      [CAMPAIGNS_FORM_FIELDS.CHANNEL]: 'WHATSAPP_API',
      [CAMPAIGNS_FORM_FIELDS.MESSAGE]: defaultMessage,
      [CAMPAIGNS_FORM_FIELDS.SEND_DATE]: new Date(),
      [CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS]: 'ALL_CONTACTS',
      [CAMPAIGNS_FORM_FIELDS.SEND_TIME]: 'DEFAULT',
    },
  });

  useEffect(() => {
    register(CAMPAIGNS_FORM_FIELDS.PUBLISH_CAMPAIGN);

    register(CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS, {
      required: 'Este campo es requerido',
    });

    register(CAMPAIGNS_FORM_FIELDS.MESSAGE, {
      required: 'El mensaje es requerido',
    });

    if (isNew) {
      register(CAMPAIGNS_FORM_FIELDS.SEGMENT, {
        required: 'El segmento es requerido',
      });
      register(CAMPAIGNS_FORM_FIELDS.CHANNEL, {
        required: 'El canal de envío es requerido',
      });

      register(CAMPAIGNS_FORM_FIELDS.SEND_DATE, {
        required: 'La fecha es requerida',
      });
      register(CAMPAIGNS_FORM_FIELDS.SEND_TIME, {
        required: 'La hora de envío es requerida',
      });
    }
  }, [isNew, register]);

  useEffect(() => {
    if (isNew) return;
    if (!campaign) return;

    setTimeout(() => {
      setValue(CAMPAIGNS_FORM_FIELDS.NAME, campaign.name);
      setValue(CAMPAIGNS_FORM_FIELDS.SEGMENT, campaign.segmentId);

      setValue(
        CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS,
        campaign.onlyIncludeNews ? 'ONLY_NEW_CONTACTS' : 'ALL_CONTACTS'
      );
    }, 200);
  }, [campaign, isNew, setValue]);

  const selectedTemplate = useMemo(() => {
    return templates?.find(template => template.name === selectedTemplateId);
  }, [selectedTemplateId, templates]);

  const handleTemplateVariablesChange = useCallback(payload => {
    templateComponentsRef.current = payload;
  }, []);

  if (!campaign && !isNew) {
    return null;
  }

  const handleSetValue = (name, value) => {
    setValue(name, value);
    clearErrors(name);
  };

  const handleCampaignCreation = (data, isDraft) => {
    const time = data[CAMPAIGNS_FORM_FIELDS.SEND_TIME];
    const date = data[CAMPAIGNS_FORM_FIELDS.SEND_DATE];

    const sendAt = new Date(
      new Date(date).toISOString().split('T')[0] + 'T' + time
    );

    const ravenService = data[CAMPAIGNS_FORM_FIELDS.CHANNEL];
    const request: CreateCampaignWebRequest = {
      name: data[CAMPAIGNS_FORM_FIELDS.NAME],
      onlyIncludeNews:
        data[CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS] === 'ONLY_NEW_CONTACTS',
      ravenService,
      segmentId: data[CAMPAIGNS_FORM_FIELDS.SEGMENT],
      state: isDraft ? 'DRAFT' : 'ACTIVE',
      scheduledAt: sendAt.toISOString(),
      setting: {
        templateSetting: {
          provider: ravenService,
          interpolationSetting: {
            values: {
              '1': 'CRM_CONTACT_NAME',
            },
          },
        },
      },
    };

    if (ravenService === 'WHATSAPP_API' && selectedTemplate) {
      request.setting.templateSetting.whatsappApiSetting = {
        template: {
          name: selectedTemplate?.name,
          components: templateComponentsRef.current,
          language: selectedTemplate.language,
        },
      };
    } else {
      request.setting.templateSetting.whatsappWebSetting = {
        template: {
          text: data[CAMPAIGNS_FORM_FIELDS.MESSAGE],
        },
      };
    }

    return createCrmCampaign(request).then(() => {
      history.push('/campaigns');
    });
  };
  const onCreateCampaign = data => {
    handleCampaignCreation(data, false);
  };
  const onDraftCampaign = data => {
    handleCampaignCreation(data, true);
  };

  const onEdit = data => {
    if (!campaign) return;
    const request: EditCampaignWebRequest = {
      name: data[CAMPAIGNS_FORM_FIELDS.NAME],
      onlyIncludeNews:
        data[CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS] === 'ONLY_NEW_CONTACTS',
    };

    if (campaign.state === 'DRAFT') {
      request.state = data[CAMPAIGNS_FORM_FIELDS.PUBLISH_CAMPAIGN]
        ? 'ACTIVE'
        : 'DRAFT';
    }

    // "Key: 'CreateCampaignWebRequest.ScheduledAt' Error:Field validation for 'ScheduledAt' failed on the 'required' tag"

    return editCrmCampaign(campaign.id, request).then(() => {
      history.push('/campaigns');
    });
  };

  const watchSegment = watch(CAMPAIGNS_FORM_FIELDS.SEGMENT);
  const watchChannel = watch(CAMPAIGNS_FORM_FIELDS.CHANNEL);
  const watchMessage = watch(CAMPAIGNS_FORM_FIELDS.MESSAGE);
  const watchDate = watch(CAMPAIGNS_FORM_FIELDS.SEND_DATE);
  const watchSendTime = watch(CAMPAIGNS_FORM_FIELDS.SEND_TIME);
  const watchIncludeContacts = watch(CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS);
  const watchPublishCampaign = watch(CAMPAIGNS_FORM_FIELDS.PUBLISH_CAMPAIGN);

  const segmentId = watchSegment ?? campaign?.segmentId;
  const selectedSegment = segments.find(segment => segment.id === segmentId);
  return (
    <div className={classes.container}>
      {loading && (
        <div className={classes.loader}>
          <InlineLoader loading={loading} />
        </div>
      )}
      <header className={classes.header}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => {
              if (isNew) return history.push('/campaigns');
              history.goBack();
            }}
          >
            <ArrowBack />
          </IconButton>
          {campaign ? `Editar: ${campaign?.name}` : 'Crear Campaña'}{' '}
          {campaign && (
            <CustomChip
              size="small"
              color={campaign.state === 'ACTIVE' ? 'primary' : 'warning'}
              label={campaign.state === 'ACTIVE' ? 'Activo' : 'Borrador'}
            />
          )}
        </Typography>
      </header>
      <aside className={classes.root}>
        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              1. Nombre de Campaña
            </Typography>

            <CustomTextField
              label="Nombre de la Campaña"
              fullWidth
              name={CAMPAIGNS_FORM_FIELDS.NAME}
              error={!!errors[CAMPAIGNS_FORM_FIELDS.NAME]}
              helperText={errors[CAMPAIGNS_FORM_FIELDS.NAME]?.message}
              inputRef={register({
                required: 'El nombre de la campaña es requerido',
              })}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
        </section>
        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>2. Segmento</Typography>

            {campaign && (
              <CustomTextField
                label="Nombre del segmento"
                fullWidth
                value={selectedSegment?.name}
                disabled
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            {!campaign && (
              <Autocomplete
                id="tags-categories"
                options={segments ? segments : []}
                getOptionLabel={segments => segments.name}
                onChange={(event, value) => {
                  handleSetValue(CAMPAIGNS_FORM_FIELDS.SEGMENT, value.id);
                  handleSetValue(CAMPAIGNS_FORM_FIELDS.SEND_DATE, new Date());
                  handleSetValue(CAMPAIGNS_FORM_FIELDS.SEND_TIME, 'DEFAULT');
                }}
                getOptionSelected={(segments, selectedValue) =>
                  watchSegment === selectedValue.id
                }
                disableClearable
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    multiline
                    error={!!errors[CAMPAIGNS_FORM_FIELDS.SEGMENT]}
                    helperText={errors[CAMPAIGNS_FORM_FIELDS.SEGMENT]?.message}
                    InputProps={{
                      ...params.InputProps,
                      className: `${params.InputProps.className} ${classes.textInput}`,
                    }}
                  />
                )}
                ChipProps={{variant: 'outlined', style: {marginTop: '8px'}}}
                classes={{
                  root: classes.categories,
                }}
              />
            )}
          </div>
        </section>
        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              3. Eligir canal de envío de la campaña
            </Typography>
            {errors[CAMPAIGNS_FORM_FIELDS.CHANNEL] && (
              <Typography className={classes.error}>
                {errors[CAMPAIGNS_FORM_FIELDS.CHANNEL].message ?? ''}
              </Typography>
            )}
            <RadioGroup
              aria-label="Tipo de canal"
              value={campaign ? campaign.ravenService : watchChannel}
              className={classes.radioGroup}
              onChange={e => {
                handleSetValue(CAMPAIGNS_FORM_FIELDS.CHANNEL, e.target.value);
              }}
            >
              {enableWhatsappWebProvider && (
                <FormControlLabel
                  value="WHATSAPP_WEB"
                  control={<Radio color="primary" disabled={!!campaign} />}
                  label="Whatsapp"
                />
              )}
              <FormControlLabel
                value="WHATSAPP_API"
                control={<Radio color="primary" disabled={!!campaign} />}
                label="Whatsapp API"
              />
            </RadioGroup>
          </div>
        </section>

        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>4. Mensaje</Typography>

            {errors[CAMPAIGNS_FORM_FIELDS.MESSAGE] && (
              <Typography className={classes.error}>
                {errors[CAMPAIGNS_FORM_FIELDS.MESSAGE].message ?? ''}
              </Typography>
            )}

            {watchChannel === 'WHATSAPP_WEB' && (
              <CustomTextEditor
                text={watchMessage}
                onChange={s => {
                  handleSetValue(CAMPAIGNS_FORM_FIELDS.MESSAGE, s);
                }}
                commandConfig={{
                  boldDisabled: true,
                  italicDisabled: true,
                }}
                extraCommands={[
                  {
                    name: 'Customer name',
                    enabled: true,
                    keyCommand: 'nombre_cliente',
                    buttonProps: {'aria-label': 'Insert customer name'},
                    icon: (
                      <div className={classes.actionButton}>
                        Nombre de cliente
                      </div>
                    ),
                    execute: (state: TextState, api: TextAreaTextApi) => {
                      api.replaceSelection('{{1}}');
                    },
                  },
                ]}
                disabled={!!campaign}
              />
            )}

            {watchChannel === 'WHATSAPP_API' && (
              <>
                <CustomSelect
                  fullWidth
                  className={classes.select}
                  value={selectedTemplateId}
                  onChange={e => {
                    setSelectedTemplateId(e.target.value);
                  }}
                  label="Template"
                >
                  {templates.map(template => (
                    <MenuItem
                      key={template?.id}
                      value={template?.name}
                      className={classes.itemSelectFontColor}
                    >
                      <Typography>{template?.name}</Typography>
                    </MenuItem>
                  ))}
                </CustomSelect>
                {selectedTemplate && (
                  <WhatsAppTemplateRenderer
                    template={selectedTemplate}
                    onChangeVariables={handleTemplateVariablesChange}
                    enabledVariables
                  />
                )}
              </>
            )}
          </div>
        </section>
        <section
          className={classes.sectionContainer}
          data-disabled={!!campaign}
        >
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              5.{' '}
              {selectedSegment && selectedSegment.segmentType === 'MANUAL'
                ? 'Fecha y hora de envío'
                : 'Horario de envío'}
            </Typography>

            {errors[CAMPAIGNS_FORM_FIELDS.SEND_DATE] && (
              <Typography className={classes.error}>
                {errors[CAMPAIGNS_FORM_FIELDS.SEND_DATE].message ?? ''}
              </Typography>
            )}

            <div className={classes.dateSection}>
              {selectedSegment && selectedSegment.segmentType === 'MANUAL' && (
                <div
                  className={classes.formField}
                  data-disabled={
                    !selectedSegment ||
                    selectedSegment.segmentType === 'AUTOMATIC'
                  }
                >
                  <div className={classes.formField}>
                    <span className={classes.subtitle}>Fecha</span>
                    <span className={classes.dateText}>
                      {getStringDate(watchDate)}
                    </span>
                  </div>

                  {campaign ? (
                    <TiendadaCalendar
                      initialDate={new Date(campaign.scheduledAt)}
                      onChange={date => {}}
                      minDate={new Date()}
                    />
                  ) : (
                    <TiendadaCalendar
                      initialDate={watchDate}
                      onChange={date => {
                        handleSetValue(CAMPAIGNS_FORM_FIELDS.SEND_DATE, date);
                      }}
                      minDate={new Date()}
                    />
                  )}
                </div>
              )}

              <div className={classes.scheduleSection}>
                <div className={classes.formField}>
                  <span className={classes.subtitle}>Hora</span>
                  <CustomSelect
                    placeholder="Selecciona un horario"
                    helperText={
                      errors[CAMPAIGNS_FORM_FIELDS.SEND_TIME] &&
                      'Selecciona un horario'
                    }
                    onChange={e => {
                      handleSetValue(
                        CAMPAIGNS_FORM_FIELDS.SEND_TIME,
                        e.target.value
                      );
                    }}
                    value={
                      campaign
                        ? getDefaultSendTime(campaign.scheduledAt)
                        : watchSendTime
                    }
                    disabled={!!campaign}
                  >
                    <MenuItem
                      className={classes.itemSelectFontColor}
                      value="DEFAULT"
                    >
                      Selecciona un horario
                    </MenuItem>
                    {STANDARD_HOUR_OPTIONS.map(option => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        className={classes.itemSelectFontColor}
                        disabled={
                          isDisabled(option.value, watchDate) &&
                          selectedSegment?.segmentType === 'MANUAL'
                        }
                      >
                        {`${option.label} - ${convertToLocalTime(
                          option.utcValue
                        )}`}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              6. ¿Quienes deberían ser incluidos en la campaña?
            </Typography>

            <TiendadaRadioButton
              name="include-contacts"
              checked={watchIncludeContacts === 'ALL_CONTACTS'}
              onChange={checked => {
                handleSetValue(
                  CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS,
                  'ALL_CONTACTS'
                );
              }}
              label="Contactos actuales y adiciones futuras"
              description="Contactos en el segmento actual y futuros contactos que podrían entrar en el futuro."
            />
            <TiendadaRadioButton
              name="include-contacts"
              checked={watchIncludeContacts === 'ONLY_NEW_CONTACTS'}
              onChange={checked => {
                handleSetValue(
                  CAMPAIGNS_FORM_FIELDS.INCLUDE_CONTACTS,
                  'ONLY_NEW_CONTACTS'
                );
              }}
              label="Solo futuras adiciones"
              description="Solo las personas que se agreguen al segmento en el futuro ingresarán a la campaña."
            />
          </div>
        </section>

        {campaign?.state === 'DRAFT' && (
          <section className={classes.sectionContainer}>
            <div className={classes.sectionInformation}>
              <Typography className={classes.subtitle}>
                7. Activar campaña
              </Typography>

              <FormField
                title={watchPublishCampaign ? 'Activo' : 'Desactivado'}
                subtitle="Activar esta opción hará que la campaña se envíe en la fecha y hora seleccionada."
                contentRight={
                  <IosSwitch
                    onChange={e => {
                      handleSetValue(
                        CAMPAIGNS_FORM_FIELDS.PUBLISH_CAMPAIGN,
                        e.target.checked
                      );
                    }}
                  />
                }
              ></FormField>
            </div>
          </section>
        )}
        <Divider className={classes.divider} />
        {isNew ? (
          <footer className={classes.footer}>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(onCreateCampaign)}
              fullWidth
            >
              Crear Campaña
            </CustomButton>

            <CustomButton
              variant="outlined"
              color="primary"
              onClick={handleSubmit(onDraftCampaign)}
              fullWidth
            >
              Guardar Borrador
            </CustomButton>
          </footer>
        ) : (
          <footer className={classes.footer}>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(onEdit)}
              fullWidth
            >
              Guardar Cambios
            </CustomButton>
          </footer>
        )}
        <Typography className={classes.sectionDescription}>
          Al hacer click en “Crear Campaña” esta se guardará para luego ser
          enviada.
        </Typography>
      </aside>
    </div>
  );
}
